import { Alert, Box, Divider, Flex } from '@chakra-ui/react';
import React, { useState, useMemo } from 'react';

import { Util, DbTable, Receivable } from '@finley-technologies/finley-types';
import { TapeBuilderUI } from '@finley-technologies/tape-builder';

import { OrgSlugToOrgMapType } from '../GraphContext';
import { BasicBorrowingBaseEvaluator } from './BasicBorrowingBaseEvaluator';
import { BorrowingBaseCalculationsDisplay } from './BorrowingBaseCalculationsDisplay';
import { CalcAgentEvaluator } from './CalcAgentEvaluator';
import {
  CFReceivableWithRow,
  CreditFacilityReceivableEvaluator,
} from './CreditFacilityReceivableEvaluator';
import { ExtendedResponseTapeEvaluator } from './ExtendedResponseTapeEvalulator';
import { CreditAgreement } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

type LoanProductToReceivablesType = Map<string, CFReceivableWithRow[] | null>;
type LoanProductToExtendedResponseTapeType = {
  [loanProductId: string]: Util.SanitizedCsv;
};

type CalcAgentOperationWithBBMProps = {
  bbm: CreditAgreement.Config.ResolvedCalcAgentMechanics;
  facilityType: DbTable.FacilityType;
  creditFacilityId: string;
  tapeBuilderNode: TapeBuilderUI.types.OperationNodes.CalcAgent.UINode;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
  orgSlugMap: OrgSlugToOrgMapType;
};
export const CalcAgentOperationWithBBM = ({
  bbm,
  facilityType,
  creditFacilityId,
  tapeBuilderNode,
  reportingDate,
  fileVersion,
  orgSlugMap,
}: CalcAgentOperationWithBBMProps) => {
  const { loanProducts, multiLoanProductConcentrationLimits } = bbm;

  // Setting up initial data so can be used as initial React states below
  const { lpReceivablesMap, loanProductsToExtendedResponseTape } = useMemo<{
    lpReceivablesMap: LoanProductToReceivablesType;
    loanProductsToExtendedResponseTape: LoanProductToExtendedResponseTapeType;
  }>(() => {
    const lpReceivablesMapOutput: LoanProductToReceivablesType = new Map();
    const loanProductsToExtendedResponseTapeOutput: LoanProductToExtendedResponseTapeType = {};

    for (const { loanProductId } of loanProducts) {
      lpReceivablesMapOutput.set(loanProductId, null);
      loanProductsToExtendedResponseTapeOutput[loanProductId] = [];
    }

    return {
      lpReceivablesMap: lpReceivablesMapOutput,
      loanProductsToExtendedResponseTape: loanProductsToExtendedResponseTapeOutput,
    };
  }, [loanProducts]);

  const [
    receivablesMap,
    setReceivablesMap,
  ] = useState<LoanProductToReceivablesType>(lpReceivablesMap);

  const [
    extendedResponseTapesForLoanProducts,
    setExtendedResponseTapesForLoanProducts,
  ] = useState<LoanProductToExtendedResponseTapeType>(
    loanProductsToExtendedResponseTape,
  );

  const [borrowingBaseDetails, setBorrowingBaseDetails] = useState<{
    borrowingBaseReceivables: Receivable.CreditFacility.BorrowingBaseReceivable[];
    concentrationLimitCalculations: CreditAgreement.Config.ConcentrationLimit.Calculation.ConcentrationLimitCalculation[];
  } | null>(null);

  const [
    borrowingBaseReportFromLoanTapeCalcs,
    setBorrowingBaseReportFromLoanTapeCalcs,
  ] = useState<DbTable.BorrowingBaseReportFromLoanTapeCalcs | null>();

  const updateMap = (
    loanProductId: string,
    receivables: CFReceivableWithRow[],
  ) => {
    const originalMap = receivablesMap.set(loanProductId, receivables);
    const newMapReference = new Map(originalMap);
    setReceivablesMap(newMapReference);
  };

  const allReceivables = useMemo<CFReceivableWithRow[]>(() => {
    const allReceivablesArray: CFReceivableWithRow[] = [];
    for (const receivables of receivablesMap.values()) {
      if (receivables !== null) {
        for (const receivable of receivables) {
          allReceivablesArray.push(receivable);
        }
      }
    }
    return allReceivablesArray;
  }, [receivablesMap]);

  if (loanProducts.length === 0) {
    return <Alert> No Loan products are defined.</Alert>;
  }

  return (
    <Box>
      <Flex direction="column">
        {loanProducts.map((lp) => {
          return (
            <CreditFacilityReceivableEvaluator
              key={`lp-${lp.loanProductId}`}
              loanProductMechanics={lp}
              creditFacilityId={creditFacilityId}
              tapeBuilderNode={tapeBuilderNode}
              reportingDate={reportingDate}
              fileVersion={fileVersion}
              orgSlugMap={orgSlugMap}
              onDone={(receivables) => {
                updateMap(lp.loanProductId, receivables);
              }}
            />
          );
        })}
        <Divider mt={4} mb={4} />
        <CalcAgentEvaluator
          borrowingBaseMechanics={bbm}
          receivables={allReceivables}
          tapeBuilderNode={tapeBuilderNode}
          reportingDate={reportingDate}
          onDone={(details) => {
            setBorrowingBaseDetails(details);
          }}
        />
        <Divider mt={4} mb={4} />
        {loanProducts.map((lp) => {
          return (
            <React.Fragment key={`lp-${lp.loanProductId}`}>
              <ExtendedResponseTapeEvaluator
                facilityType={facilityType}
                loanProductMechanics={lp}
                tapeBuilderNode={tapeBuilderNode}
                onDone={(extendedResponseTape) => {
                  setExtendedResponseTapesForLoanProducts({
                    ...extendedResponseTapesForLoanProducts,
                    [lp.loanProductId]: extendedResponseTape,
                  });
                }}
                borrowingBaseReceivables={
                  borrowingBaseDetails?.borrowingBaseReceivables || []
                }
                reportingDate={reportingDate}
                multiLoanProductConcentrationLimits={
                  multiLoanProductConcentrationLimits
                }
              />
              {/* @dani todo: reenable after calc agent servicer report backend redone */}
              {/* {extendedResponseTapesForLoanProducts[lp.loanProductId] &&
                !!extendedResponseTapesForLoanProducts[lp.loanProductId]
                  .length && (
                  <CalcAgentQAEmailServicerReportPreviewButton
                    extendedResponseTape={
                      extendedResponseTapesForLoanProducts[lp.loanProductId]
                    }
                    loanProductMechanics={lp}
                    reportingDate={reportingDate}
                    tapeBuilderNode={tapeBuilderNode}
                  />
                )} */}
            </React.Fragment>
          );
        })}
        <Divider mt={4} mb={4} />
        <BasicBorrowingBaseEvaluator
          borrowingBaseMechanics={bbm}
          borrowingBaseReceivables={
            borrowingBaseDetails?.borrowingBaseReceivables || []
          }
          concentrationLimitCalculations={
            borrowingBaseDetails?.concentrationLimitCalculations || []
          }
          tapeBuilderNode={tapeBuilderNode}
          reportingDate={reportingDate}
          onDone={(output) => {
            setBorrowingBaseReportFromLoanTapeCalcs(output);
          }}
        />
        <Divider mt={4} mb={4} />
        {borrowingBaseReportFromLoanTapeCalcs && (
          <BorrowingBaseCalculationsDisplay
            borrowingBaseReportFromLoanTapeCalcs={
              borrowingBaseReportFromLoanTapeCalcs
            }
          />
        )}
      </Flex>
    </Box>
  );
};
